'use client'
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import { twMerge } from 'tailwind-merge'
import PropTypes from 'prop-types'
import { forwardRef, useState } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'

const InputMain = ({ className, children }) => {
  return (
    <div
      className={twMerge(
        'sm:grid sm:gap-4 sm:items-start sm:border-b sm:border-[#EAECF0] pb-5',
        className,
      )}
    >
      {children}
    </div>
  )
}

InputMain.Label = function InputMainLabel({
  label,
  className,
  htmlFor,
  sublabel,
  tooltip,
  required,
}) {
  if (!label) return null

  return (
    <>
      <label
        htmlFor={htmlFor}
        className={twMerge(
          'block text-sm font-medium text-v8-ink',
          className,
        )}
      >
        <span className="relative inline-block">
          {label}
          {!!tooltip && (
            <div className="absolute inset-y-0 z-10 inline-flex items-center pl-5 cursor-pointer -right-6 group">
              <QuestionMarkCircleIcon
                className="w-4 h-4 text-v8-mut"
                aria-hidden="true"
              />
            </div>
          )}
        </span>

        {sublabel && (
          <span className="block font-normal text-[#667085]">{sublabel}</span>
        )}
        {required && <span className="pl-1 text-red-600">*</span>}
      </label>
    </>
  )
}

InputMain.Input = function InputMainInput(
  {
    id,
    addon,
    addonClassName,
    addons,
    variant,
    additionalCheckbox,
    inputType,
    className,
    inputClassName,
    inputContainer,
    tooltip,
    error,
    inputIcon,
    title,
    placeholder,
    innerComponent,
    errorClassName,
    onSelect,
    ...props
  },
  ref,
) {
  const [message, setMessage] = useState(false)
  return (
    <div className={className}>
      {variant === 'courses-add-on' && !!addons && addons.length > 0 && (
        <div className="">
          <label className="block text-[#98A2B3]">
            <select
              type={inputType}
              id={id}
              name={id}
              className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md text-sm shadow-sm text-ellipsis pr-8 placeholder-slate-400 focus:border-v8-blue focus:ring-v8-blue focus:outline-none  focus:ring-1  disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none invalid:border-pink-500 invalid:text-pink-600 focus:invalid:border-pink-500 focus:invalid:ring-pink-500"
              onChange={onSelect}
            >
              <option value="">Select-</option>
              {addons.map((addonItem, ind) => {
                return (
                  <option key={ind} value={addonItem}>
                    {addonItem}
                  </option>
                )
              })}
            </select>
          </label>
        </div>
      )}
      {variant === 'phone-add-on' && !!addons && addons.length > 0 && (
        <div>
          <div className="mt-1 sm:col-span-2 sm:mt-0">
            <div className="relative mt-1 rounded-md shadow-sm flex ">
              <div className="absolute w-[110px] -left-[0.5px] h-[calc(100%-2px)] top-px">
                {innerComponent && (
                  <select
                    id={innerComponent.id}
                    name={innerComponent.name}
                    autoComplete={innerComponent.autoComplete}
                    className="h-full rounded-md text-v8-mut outline-0 ring-0 sm:text-sm border-0"
                    onChange={onSelect}
                  >
                    {addons.map((addonItem, ind) => {
                      return (
                        <option key={ind} value={addonItem}>
                          {addonItem}
                        </option>
                      )
                    })}
                  </select>
                )}
              </div>
              <input
                type={inputType}
                name={id}
                id={id}
                className="block pl-[113px] w-[100%] placeholder-slate-400 focus:border-v8-blue  focus:ring-v8-blue rounded-md  text-v8-ink/100 font-normal text-base leading-6 border-v8-line sm:text-sm"
                placeholder={placeholder}
                ref={ref}
                {...props}
              />
            </div>
          </div>
        </div>
      )}
      {variant === 'add-on' && !!addon && (
        <div>
          <div
            className={twMerge(
              'flex mt-1 rounded-lg shadow-sm sm:mt-0',
              inputContainer,
            )}
          >
            <span className="inline-flex items-center px-3.5 py-3 text-sm md:text-base text-v8-mut border border-r-0 border-v8-line rounded-l-lg bg-cblue-50">
              {addon}
            </span>
            <input
              id={id}
              type={inputType}
              placeholder={placeholder}
              className={twMerge(
                'flex-1 block w-full min-w-0 text-v8-ink placeholder:text-v8-mut border-v8-line rounded-none focus:ring-v8-blue focus:border-v8-blue rounded-r-lg sm:text-base',
                inputClassName,
                props.disabled && 'bg-[#F9FAFB]',
              )}
              ref={ref}
              {...props}
            />
          </div>
          {error && (
            <div
              className={twMerge(
                'mt-2 text-xs text-red-600 text-start',
                errorClassName,
              )}
            >
              {error}
            </div>
          )}
        </div>
      )}
      {variant === 'add-on-reverse-simple' && !!addon && (
        <div>
          <div
            className={twMerge(
              'flex mt-1 rounded-lg shadow-sm sm:mt-0',
              inputContainer,
            )}
          >
            <input
              id={id}
              type={inputType}
              className={twMerge(
                'flex-1 block w-full min-w-0 text-v8-ink placeholder:text-v8-mut border-v8-line rounded-none focus:ring-v8-blue focus:border-v8-blue rounded-l-lg sm:text-base',
                inputClassName,
                props.disabled && 'bg-[#F9FAFB]',
              )}
              ref={ref}
              {...props}
            />
            <span
              className={twMerge(
                'inline-flex items-center px-3.5 py-3 text-sm md:text-base text-v8-mut border border-l-0 border-v8-line rounded-r-lg bg-cblue-50',
                addonClassName || '',
              )}
            >
              {addon}
            </span>
          </div>
          {error && (
            <div
              className={twMerge(
                'mt-2 text-xs text-red-600 text-start',
                errorClassName,
              )}
            >
              {error}
            </div>
          )}
        </div>
      )}
      {variant === 'add-on-reverse' && !!addon && (
        <div
          className={twMerge(
            'flex mt-1 rounded-lg shadow-sm sm:mt-0',
            inputContainer,
          )}
        >
          <div className="relative flex items-stretch flex-grow focus-within:z-10">
            <input
              id={id}
              type={inputType}
              className={twMerge(
                'flex-1 block w-full min-w-0 text-v8-ink placeholder:text-v8-mut border-v8-line rounded-none focus:ring-v8-blue focus:border-v8-blue rounded-r-lg sm:text-base',
                inputClassName,
                props.disabled && 'bg-[#F9FAFB]',
              )}
              ref={ref}
              {...props}
            />
          </div>
          <CopyToClipboard text={props.value ?? props.defaultValue}>
            <button
              onClick={() => {
                setMessage(true)
                setTimeout(() => setMessage(false), 3000)
              }}
              type="button"
              className="relative inline-flex items-center px-4 py-2 -ml-px space-x-2 text-sm font-medium text-[#344054] border border-v8-line rounded-r-md bg-[#F9FAFB] hover:bg-v8-blue/5 focus:outline-none focus:ring-1 focus:ring-v8-blue focus:border-v8-blue"
            >
              {addon}
            </button>
          </CopyToClipboard>
        </div>
      )}
      {message && (
        <span className="flex justify-end text-sm text-red-500">Copied!</span>
      )}
      {variant === 'default' && (
        <>
          <div
            className={twMerge(
              'flex mt-1 rounded-lg shadow-sm sm:mt-0',
              inputContainer,
            )}
          >
            <input
              id={id}
              type={inputType}
              placeholder={placeholder}
              className={twMerge(
                'focus:ring-v8-blue focus:border-v8-blue text-v8-ink placeholder:text-v8-mut block w-full text-base border-v8-line rounded-lg',
                inputClassName,
                inputIcon && 'pl-11',
                props.disabled && 'bg-[#F9FAFB]',
              )}
              onChange={props.onChange}
              ref={ref}
              {...props}
            />
            {!!tooltip && (
              <div className="absolute inset-y-0 right-0 z-10 inline-flex items-center pr-3 cursor-pointer group">
                <QuestionMarkCircleIcon
                  className="w-4 h-4 text-v8-mut"
                  aria-hidden="true"
                />
              </div>
            )}

            {!!inputIcon && (
              <div className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none">
                <span className="text-v8-ink text-xl">{inputIcon}</span>
              </div>
            )}
          </div>
          {error && (
            <div
              className={twMerge(
                'mt-2 text-xs text-red-600 text-start',
                errorClassName,
              )}
            >
              {error}
            </div>
          )}
        </>
      )}
      {additionalCheckbox && additionalCheckbox}
    </div>
  )
}

InputMain.Input = forwardRef(InputMain.Input)

InputMain.Textarea = function InputMainTextarea(
  { id, className, textareaClassName, rows, error, ...props },
  ref,
) {
  return (
    <div className={className}>
      <div className="relative flex rounded-md shadow-sm">
        <textarea
          id={id}
          type="text"
          className={twMerge(
            'focus:ring-v8-blue focus:border-v8-blue text-v8-ink placeholder:text-v8-mut block w-full text-base border-v8-line rounded-lg',
            textareaClassName,
            props.disabled && 'bg-[#F9FAFB]',
          )}
          onChange={props.onChange}
          rows={rows}
          ref={ref}
          {...props}
        />
      </div>
      {error && (
        <div className="mt-2 text-xs text-red-600 text-start">
          <span className="text-xs text-red-600 ">{error}</span>
        </div>
      )}
    </div>
  )
}

InputMain.Textarea = forwardRef(InputMain.Textarea)

////////////////////////

InputMain.Input.defaultProps = {
  inputType: 'text',
  variant: 'default',
}

////////////////////////

InputMain.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
}

InputMain.Label.propTypes = {
  htmlFor: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  sublabel: PropTypes.string,
  tooltip: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string.isRequired,
  }),
  className: PropTypes.string,
  required: PropTypes.bool,
}

InputMain.Input.propTypes = {
  inputType: PropTypes.string,
  variant: PropTypes.string,
  id: PropTypes.string.isRequired,
  addon: PropTypes.string || PropTypes.node,
  additionalCheckbox: PropTypes.node,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  tooltip: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
  }),
}

export default InputMain
